import React from "react"
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Image,
  Font,
  Link,
} from "@react-pdf/renderer"
import { theme } from "../../src/theme"
import { addhttp } from "../addhttp"
import { ArrayTranslate } from "../../src/components/molecules/JobDetail/TopSectionCard"
import { t } from "i18next"
import {
  PdfJobOfferPoint,
  PdfTopCardSection,
  PdfTopSectionCardSecond,
} from "../PDFComponents"

/**
 * PDF用テキスト変換: フォントに含まれない記号を代替文字に置換する
 * NotoSansJP TTFには絵文字・Dingbats系グリフが含まれないため変換が必要
 * またバリエーションセレクタ(U+FE0E/FE0F)はPDFレンダラーで処理不可のため除去
 */
export const sanitizeTextForPdf = (text: any): string => {
  if (text == null) return ""
  const str = typeof text === "string" ? text : String(text)
  return (
    str
      // バリエーションセレクタを先に除去 (U+FE0E text / U+FE0F emoji)
      .replace(/[\uFE0E\uFE0F]/g, "")
      // 幾何学記号 (実データで使用されている)
      .replace(/\u25AA/g, "■") // ▪ BLACK SMALL SQUARE → 全角黒四角
      .replace(/\u25AB/g, "□") // ▫ WHITE SMALL SQUARE → 全角白四角
      .replace(/\u25FC/g, "■") // ◼ BLACK MEDIUM SQUARE
      .replace(/\u25FB/g, "□") // ◻ WHITE MEDIUM SQUARE
      // 星・装飾記号
      .replace(/\u2B50/g, "★") // ⭐ WHITE MEDIUM STAR → 全角黒星
      .replace(/\u2B51/g, "★") // ⯑ SMALL STAR
      .replace(/\u2606/g, "☆") // ☆ WHITE STAR
      .replace(/\u2605/g, "★") // ★ BLACK STAR
      .replace(/\u2728/g, "★") // ✨ SPARKLES
      // Ballot Box 系 (U+2610-2612)
      .replace(/\u2611/g, "[v]") // ☑ BALLOT BOX WITH CHECK
      .replace(/\u2612/g, "[x]") // ☒ BALLOT BOX WITH X
      .replace(/\u2610/g, "[ ]") // ☐ BALLOT BOX
      // Check / X mark 系
      .replace(/\u2713/g, "[v]") // ✓ CHECK MARK
      .replace(/\u2714/g, "[v]") // ✔ HEAVY CHECK MARK
      .replace(/\u2705/g, "[v]") // ✅ WHITE HEAVY CHECK MARK
      .replace(/\u2717/g, "[x]") // ✗ BALLOT X
      .replace(/\u2718/g, "[x]") // ✘ HEAVY BALLOT X
      .replace(/\u2716/g, "[x]") // ✖ HEAVY MULTIPLICATION X
      .replace(/\u2613/g, "[x]") // ☓ SALTIRE
      // 丸記号
      .replace(/\u2B55/g, "○") // ⭕ HEAVY LARGE CIRCLE
      .replace(/\u274C/g, "×") // ❌ CROSS MARK
      .replace(/\u274E/g, "×") // ❎ NEGATIVE SQUARED CROSS MARK
      // その他の絵文字をフォールバック除去 (Supplementary Private Use等以外の一般絵文字)
      .replace(/[\u{1F300}-\u{1F9FF}]/gu, "")
  )
}

const chunkSubstr = (str, size) => {
  const numChunks = Math.ceil(str.length / size)
  const chunks = new Array(numChunks)

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str.substr(o, size)
  }

  return chunks
}

interface PdfDownloadProps {
  jobData?: any
  signedObject?: any
  isCompany?: any
  interviewImageObject?: any
}

export const PdfDownload = ({
  jobData,
  signedObject,
  isCompany,
  interviewImageObject,
}: PdfDownloadProps) => {
  Font.register({
    family: "Noto Sans JP",
    fonts: [
      {
        src: "https://fonts.gstatic.com/s/notosansjp/v56/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFBEj75s.ttf",
        fontWeight: 400,
      },
      {
        src: "https://fonts.gstatic.com/s/notosansjp/v56/-F6jfjtqLzI2JPCgQBnw7HFyzSD-AsregP8VFPYk75s.ttf",
        fontWeight: 700,
      },
    ],
  })
  Font.registerHyphenationCallback((word) => {
    if (word.length > 16) {
      return chunkSubstr(word, 14)
    }
    return [word]
  })
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.tags}>
          {jobData?.contract_type && (
            <Text style={styles.tagGreen}>{jobData?.contract_type}</Text>
          )}
        </View>
        <View style={styles.title}>
          <Text>
            {jobData?.agent_company?.name
              ? jobData?.agent_company.name
              : jobData?.company?.name}
            {` ${jobData?.one_phrase}`}
          </Text>
        </View>

        <View style={styles.companyUrl}>
          {jobData?.company?.url || jobData?.agent_company?.url ? (
            <Link
              style={styles.link}
              src={
                jobData?.company_id
                  ? addhttp(jobData?.company?.url)
                  : addhttp(jobData?.agent_company?.url)
              }
            >
              {jobData?.company_id
                ? jobData?.company?.url
                : jobData?.agent_company?.url}
            </Link>
          ) : null}
          {jobData?.industry_type?.length ||
          jobData?.industry_type_new?.length ? (
            <Text style={styles.industryType}>
              (
              {ArrayTranslate(
                jobData?.industry_type_new?.length
                  ? jobData?.industry_type_new
                  : jobData?.industry_type
              )}
              )
            </Text>
          ) : null}
        </View>
        <View style={styles.main}>
          <View style={styles.left}>
            <Image
              style={styles.img}
              src={signedObject || "/assets/icons/logo-job-placeholder.png"}
            />
            <View style={styles.jobDetail}>
              <Text style={styles.companyInfo}>
                {t("Recruiting company")}:
                {jobData?.company_id
                  ? jobData?.company?.name
                  : jobData?.agent_company?.agent?.company_name}
              </Text>
              {/* <Text style={styles.companyInfo}>
                求人最終更新日 :
                {moment(jobData?.updated_at).format("YYYY-MM-DD")}
              </Text> */}
            </View>
          </View>
          <View style={styles.right}>
            <PdfTopCardSection jobData={jobData} />
            <PdfTopSectionCardSecond jobData={jobData} />
          </View>
        </View>
        <PdfJobOfferPoint
          jobData={jobData}
          interviewImageObject={interviewImageObject}
          isCompany={isCompany}
        />
      </Page>
    </Document>
  )
}

const styles = StyleSheet.create({
  page: {
    fontFamily: "Noto Sans JP",
    fontSize: 8,
    backgroundColor: "#fff",
    padding: "20pt",
  },
  tags: {
    paddingBottom: "6pt",
    display: "flex",
    flexDirection: "row",
    gap: "10px",
  },
  tagPurple: {
    padding: "0 2pt",
    color: "#531dab",
    backgroundColor: "#f9f0ff",
    border: "1pt solid #d3adf7",
    borderRadius: "2pt",
    fontSize: 6,
  },
  tagGreen: {
    padding: "0 2pt",
    color: "#08979c",
    backgroundColor: "#e6fffb",
    border: "1pt solid #87e8de",
    borderRadius: "2pt",
    fontSize: 6,
  },
  title: {
    fontSize: "10pt",
    fontWeight: "bold",
    paddingRight: "10pt",
  },
  companyUrl: {
    flexDirection: "row",
    gap: "9pt",
    marginBottom: "15pt",
  },
  industryType: {
    color: "#7d857c",
  },
  main: {
    flexDirection: "row",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginTop: "20pt",
  },
  left: {
    flexDirection: "column",
    marginRight: "10pt",
  },
  img: {
    backgroundColor: "#f0f2f5",
    objectFit: "contain",
    objectPosition: "center",
    height: "150pt",
    width: "200pt",
  },
  jobDetail: {
    flexDirection: "column",
    marginTop: "10pt",
  },
  companyInfo: {
    marginBottom: "10pt",
    fontSize: 8,
  },
  link: {
    color: theme.blue2,
    textDecoration: "none",
  },
  right: {},
})
